"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluggableComboChart = void 0;
var tslib_1 = require("tslib");
// (C) 2019 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var react_dom_1 = require("react-dom");
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var set_1 = tslib_1.__importDefault(require("lodash/set"));
var without_1 = tslib_1.__importDefault(require("lodash/without"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var sdk_ui_charts_1 = require("@gooddata/sdk-ui-charts");
var axis_1 = require("../../../constants/axis");
var bucket_1 = require("../../../constants/bucket");
var properties_1 = require("../../../constants/properties");
var supportedProperties_1 = require("../../../constants/supportedProperties");
var uiConfig_1 = require("../../../constants/uiConfig");
var bucketConfig_1 = require("../../../utils/bucketConfig");
var bucketHelper_1 = require("../../../utils/bucketHelper");
var bucketRules_1 = require("../../../utils/bucketRules");
var propertiesHelper_1 = require("../../../utils/propertiesHelper");
var sort_1 = require("../../../utils/sort");
var comboChartUiConfigHelper_1 = require("../../../utils/uiConfigHelpers/comboChartUiConfigHelper");
var LineChartBasedConfigurationPanel_1 = tslib_1.__importDefault(require("../../configurationPanels/LineChartBasedConfigurationPanel"));
var PluggableBaseChart_1 = require("../baseChart/PluggableBaseChart");
var sdk_model_1 = require("@gooddata/sdk-model");
var PluggableComboChart = /** @class */ (function (_super) {
    tslib_1.__extends(PluggableComboChart, _super);
    function PluggableComboChart(props) {
        var _a, _b, _c;
        var _this = _super.call(this, props) || this;
        _this.primaryChartType = sdk_ui_1.VisualizationTypes.COLUMN;
        _this.secondaryChartType = sdk_ui_1.VisualizationTypes.COLUMN;
        _this.type = sdk_ui_1.VisualizationTypes.COMBO;
        _this.axis = axis_1.AXIS.DUAL;
        _this.secondaryAxis = axis_1.AXIS_NAME.SECONDARY_Y;
        var propertiesControls = (_a = props.visualizationProperties) === null || _a === void 0 ? void 0 : _a.controls;
        _this.primaryChartType = (_b = propertiesControls === null || propertiesControls === void 0 ? void 0 : propertiesControls.primaryChartType) !== null && _b !== void 0 ? _b : sdk_ui_1.VisualizationTypes.COLUMN;
        _this.secondaryChartType = (_c = propertiesControls === null || propertiesControls === void 0 ? void 0 : propertiesControls.secondaryChartType) !== null && _c !== void 0 ? _c : sdk_ui_1.VisualizationTypes.COLUMN;
        _this.supportedPropertiesList = _this.getSupportedPropertiesList();
        _this.defaultControlsProperties = {
            stackMeasures: _this.isStackMeasuresByDefault(),
        };
        _this.initializeProperties(props.visualizationProperties);
        return _this;
    }
    PluggableComboChart.prototype.getSupportedPropertiesList = function () {
        return supportedProperties_1.COMBO_CHART_SUPPORTED_PROPERTIES[this.axis] || [];
    };
    PluggableComboChart.prototype.getUiConfig = function () {
        return cloneDeep_1.default(tslib_1.__assign(tslib_1.__assign({}, uiConfig_1.COMBO_CHART_UICONFIG), { optionalStacking: {
                supported: true,
                disabled: sdk_ui_charts_1.isLineChart(this.primaryChartType),
                stackMeasures: this.isStackMeasuresByDefault(),
            } }));
    };
    PluggableComboChart.prototype.getExtendedReferencePoint = function (referencePoint) {
        var _a, _b, _c, _d, _e, _f;
        var clonedReferencePoint = cloneDeep_1.default(referencePoint);
        var properties = this.configureChartTypes(clonedReferencePoint);
        this.primaryChartType = (_b = (_a = properties === null || properties === void 0 ? void 0 : properties.controls) === null || _a === void 0 ? void 0 : _a.primaryChartType) !== null && _b !== void 0 ? _b : sdk_ui_1.VisualizationTypes.COLUMN;
        this.secondaryChartType = (_d = (_c = properties === null || properties === void 0 ? void 0 : properties.controls) === null || _c === void 0 ? void 0 : _c.secondaryChartType) !== null && _d !== void 0 ? _d : sdk_ui_1.VisualizationTypes.COLUMN;
        var newReferencePoint = tslib_1.__assign(tslib_1.__assign({}, clonedReferencePoint), { properties: properties, uiConfig: this.getUiConfig() });
        this.defaultControlsProperties = {
            stackMeasures: this.isStackMeasuresByDefault(),
        };
        this.configureBuckets(newReferencePoint);
        newReferencePoint = propertiesHelper_1.setSecondaryMeasures(newReferencePoint, this.secondaryAxis);
        this.axis = (_f = (_e = newReferencePoint === null || newReferencePoint === void 0 ? void 0 : newReferencePoint.uiConfig) === null || _e === void 0 ? void 0 : _e.axis) !== null && _f !== void 0 ? _f : axis_1.AXIS.PRIMARY;
        this.supportedPropertiesList = this.getSupportedPropertiesList();
        newReferencePoint = comboChartUiConfigHelper_1.setComboChartUiConfig(newReferencePoint, this.intl, this.type);
        newReferencePoint = bucketConfig_1.configurePercent(newReferencePoint, this.isPercentDisabled(newReferencePoint));
        newReferencePoint = bucketConfig_1.configureOverTimeComparison(newReferencePoint, !!this.featureFlags["enableWeekFilters"]);
        newReferencePoint = propertiesHelper_1.getReferencePointWithSupportedProperties(newReferencePoint, this.supportedPropertiesList);
        newReferencePoint = bucketHelper_1.applyUiConfig(newReferencePoint);
        newReferencePoint = sort_1.removeSort(newReferencePoint);
        return Promise.resolve(bucketHelper_1.sanitizeFilters(newReferencePoint));
    };
    PluggableComboChart.prototype.isStackMeasuresByDefault = function () {
        return sdk_ui_charts_1.isAreaChart(this.primaryChartType);
    };
    PluggableComboChart.prototype.configureBuckets = function (extReferencePoint) {
        var _a, _b, _c, _d, _e, _f, _g;
        var buckets = (_a = extReferencePoint === null || extReferencePoint === void 0 ? void 0 : extReferencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var attributes = bucketHelper_1.getAllAttributeItemsWithPreference(buckets, [
            sdk_ui_1.BucketNames.TREND,
            sdk_ui_1.BucketNames.VIEW,
        ]).slice(0, 1);
        var measures = [];
        var secondaryMeasures = [];
        // ref. point has both my buckets -> reuse them fully
        if (bucketHelper_1.hasBucket(buckets, sdk_ui_1.BucketNames.MEASURES) && bucketHelper_1.hasBucket(buckets, sdk_ui_1.BucketNames.SECONDARY_MEASURES)) {
            measures = bucketHelper_1.getBucketItemsByType(buckets, sdk_ui_1.BucketNames.MEASURES, [bucket_1.METRIC]);
            secondaryMeasures = bucketHelper_1.getBucketItemsByType(buckets, sdk_ui_1.BucketNames.SECONDARY_MEASURES, [bucket_1.METRIC]);
            var restMeasures = bucketHelper_1.getBucketItemsWithExcludeByType(buckets, [sdk_ui_1.BucketNames.MEASURES, sdk_ui_1.BucketNames.SECONDARY_MEASURES], [bucket_1.METRIC]);
            secondaryMeasures = secondaryMeasures.concat(restMeasures);
        }
        else {
            // transform from dual axis chart to combo chart
            var allMeasures = bucketHelper_1.getMeasureItems(buckets);
            secondaryMeasures = bucketHelper_1.getAllMeasuresShowOnSecondaryAxis(buckets);
            measures = without_1.default.apply(void 0, tslib_1.__spreadArrays([allMeasures], secondaryMeasures));
        }
        var isDualAxisEnabled = propertiesHelper_1.isDualAxisOrSomeSecondaryAxisMeasure(extReferencePoint, secondaryMeasures);
        set_1.default(extReferencePoint, properties_1.PROPERTY_CONTROLS_DUAL_AXIS, isDualAxisEnabled);
        var primaryChartType = (_d = (_c = (_b = extReferencePoint === null || extReferencePoint === void 0 ? void 0 : extReferencePoint.properties) === null || _b === void 0 ? void 0 : _b.controls) === null || _c === void 0 ? void 0 : _c.primaryChartType) !== null && _d !== void 0 ? _d : sdk_ui_1.VisualizationTypes.COLUMN;
        var secondaryChartType = (_g = (_f = (_e = extReferencePoint === null || extReferencePoint === void 0 ? void 0 : extReferencePoint.properties) === null || _e === void 0 ? void 0 : _e.controls) === null || _f === void 0 ? void 0 : _f.secondaryChartType) !== null && _g !== void 0 ? _g : sdk_ui_1.VisualizationTypes.LINE;
        set_1.default(extReferencePoint, bucket_1.BUCKETS, [
            {
                localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                items: bucketHelper_1.setMeasuresShowOnSecondaryAxis(measures, false),
                chartType: primaryChartType,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.SECONDARY_MEASURES,
                items: bucketHelper_1.setMeasuresShowOnSecondaryAxis(secondaryMeasures, isDualAxisEnabled),
                chartType: secondaryChartType,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.VIEW,
                items: attributes,
            },
        ]);
    };
    PluggableComboChart.prototype.configureChartTypes = function (referencePoint) {
        var _a, _b, _c, _d, _e, _f, _g;
        var buckets = (_a = referencePoint === null || referencePoint === void 0 ? void 0 : referencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var controls = (_c = (_b = referencePoint === null || referencePoint === void 0 ? void 0 : referencePoint.properties) === null || _b === void 0 ? void 0 : _b.controls) !== null && _c !== void 0 ? _c : {};
        var primaryChartType = (_e = (_d = bucketHelper_1.findBucket(buckets, sdk_ui_1.BucketNames.MEASURES)) === null || _d === void 0 ? void 0 : _d.chartType) !== null && _e !== void 0 ? _e : (controls.primaryChartType || sdk_ui_1.VisualizationTypes.COLUMN);
        var secondaryChartType = (_g = (_f = bucketHelper_1.findBucket(buckets, sdk_ui_1.BucketNames.SECONDARY_MEASURES)) === null || _f === void 0 ? void 0 : _f.chartType) !== null && _g !== void 0 ? _g : (controls.secondaryChartType || sdk_ui_1.VisualizationTypes.LINE);
        if (primaryChartType || secondaryChartType) {
            return tslib_1.__assign(tslib_1.__assign({}, referencePoint.properties), { controls: tslib_1.__assign(tslib_1.__assign({}, controls), { primaryChartType: primaryChartType,
                    secondaryChartType: secondaryChartType }) });
        }
        return referencePoint.properties;
    };
    PluggableComboChart.prototype.isPercentDisabled = function (extReferencePoint) {
        var _a;
        if (this.axis === axis_1.AXIS.DUAL) {
            return false;
        }
        var buckets = (_a = extReferencePoint === null || extReferencePoint === void 0 ? void 0 : extReferencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var primaryMasterMeasures = bucketRules_1.getMasterMeasuresCount(buckets, sdk_ui_1.BucketNames.MEASURES);
        var secondaryMasterMeasures = bucketRules_1.getMasterMeasuresCount(buckets, sdk_ui_1.BucketNames.SECONDARY_MEASURES);
        // disable percent if there is more than one measure on primary/secondary y-axis
        return primaryMasterMeasures + secondaryMasterMeasures > 1;
    };
    PluggableComboChart.prototype.isDataPointsControlDisabled = function (insight) {
        var measureBucketsOfNonColumnCharts = [
            [this.primaryChartType, sdk_ui_1.BucketNames.MEASURES],
            [this.secondaryChartType, sdk_ui_1.BucketNames.SECONDARY_MEASURES],
        ]
            .filter(function (_a) {
            var chartType = _a[0];
            return chartType !== sdk_ui_1.VisualizationTypes.COLUMN;
        })
            .map(function (_a) {
            var bucketId = _a[1];
            return sdk_model_1.insightBuckets(insight, bucketId);
        });
        return (measureBucketsOfNonColumnCharts.length === 0 ||
            measureBucketsOfNonColumnCharts.every(function (bucket) { return sdk_model_1.bucketsIsEmpty(bucket); }));
    };
    PluggableComboChart.prototype.renderConfigurationPanel = function (insight) {
        if (document.querySelector(this.configPanelElement)) {
            react_dom_1.render(react_1.default.createElement(LineChartBasedConfigurationPanel_1.default, { locale: this.locale, references: this.references, properties: this.visualizationProperties, propertiesMeta: this.propertiesMeta, insight: insight, colors: this.colors, pushData: this.handlePushData, type: this.type, isError: this.getIsError(), isLoading: this.isLoading, featureFlags: this.featureFlags, axis: this.axis, panelConfig: {
                    isDataPointsControlDisabled: this.isDataPointsControlDisabled(insight),
                }, dataLabelDefaultValue: "auto" }), document.querySelector(this.configPanelElement));
        }
    };
    return PluggableComboChart;
}(PluggableBaseChart_1.PluggableBaseChart));
exports.PluggableComboChart = PluggableComboChart;
//# sourceMappingURL=PluggableComboChart.js.map